import {
    HttpErrorResponse,
    HttpResponse,
    HttpStatusCode,
} from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { GoogleApiError } from '../models/google-api-error';

/**
 * Processes request. Refreshes token and retries request (once) if it fails with {@link HttpStatusCode.Unauthorized}.
 * @param requestGetter Function that returns request to process.
 * @param tokenRefresher Function that refreshes token. It's only called when request fails with {@link HttpStatusCode.Unauthorized}.
 * @returns Response body as T. Throws {@link GoogleApiError} if final request fails with other status code than 2xx.
 */
export async function processRequest<T>(
    requestGetter: () => Promise<
        Observable<HttpResponse<T> | HttpErrorResponse>
    >,
    tokenRefresher: () => Promise<void>
): Promise<T> {
    let response = await lastValueFrom(await requestGetter());

    // unauthorized - refresh token and retry request
    if (response.status === HttpStatusCode.Unauthorized) {
        await tokenRefresher();
        response = await lastValueFrom(await requestGetter());
    }

    // something went wrong on the client-side such as a network error
    if (response.status === 0) {
        throw response;
    }

    // other error (or again unauthorized after token refresh) - throw error
    if (!response.ok) {
        throw new GoogleApiError(
            response.status,
            response instanceof HttpErrorResponse
                ? response.error
                : (response.body as any)
        );
    }

    // success - return response body
    return response.body!;
}
